import type { Rectangle } from '../../../renderer/src/video-presentation';

export interface PlaceTitlePlacement extends Rectangle { readonly scale: number }
const right = (box: Rectangle) => box.x + box.width;
const bottom = (box: Rectangle) => box.y + box.height;
const clamp = (value: number, low: number, high: number) => Math.max(low, Math.min(high, value));

/** Finds clear horizontal spans between the measured plates, including beside a side rail. */
function clearAreas(area: Rectangle, plates: readonly Rectangle[]): Rectangle[] {
  const edges = [...new Set([area.y, bottom(area), ...plates.flatMap(plate =>
    [clamp(plate.y, area.y, bottom(area)), clamp(bottom(plate), area.y, bottom(area))])])].sort((a, b) => a - b);
  const result: Rectangle[] = [];
  for (let top = 0; top < edges.length - 1; top++) for (let end = top + 1; end < edges.length; end++) {
    const y = edges[top]!;
    const height = edges[end]! - y;
    const occupied = plates.filter(plate => plate.y < y + height && bottom(plate) > y)
      .map(plate => [clamp(plate.x, area.x, right(area)), clamp(right(plate), area.x, right(area))] as const)
      .sort((a, b) => a[0] - b[0]);
    let x = area.x;
    for (const [left, endX] of [...occupied, [right(area), right(area)] as const]) {
      if (left > x) result.push({ x, y, width: left - x, height });
      x = Math.max(x, endX);
    }
  }
  return result;
}

/** Keeps the complete lockup clear of every plate, preferring the upper half below the title plate. */
export function placeTitlePlacement(frame: Pick<Rectangle, 'width' | 'height'>, text: Pick<Rectangle, 'width' | 'height'>,
  plates: readonly Rectangle[], topTitleBottom = 0): PlaceTitlePlacement | null {
  if (frame.width <= 0 || frame.height <= 0 || text.width <= 0 || text.height <= 0) return null;
  const gap = Math.min(frame.width, frame.height) * .02;
  const safe: Rectangle = { x: frame.width * .08, y: gap, width: frame.width * .84, height: frame.height - gap * 2 };
  const expanded = plates.filter(plate => plate.width > 0 && plate.height > 0).map(plate => ({
    x: plate.x - gap, y: plate.y - gap, width: plate.width + gap * 2, height: plate.height + gap * 2,
  }));
  const start = Math.max(safe.y, topTitleBottom > 0 ? topTitleBottom + gap : 0);
  const preferred = { ...safe, y: start, height: Math.max(0, frame.height / 2 - start) };
  const belowTitle = { ...safe, y: start, height: Math.max(0, bottom(safe) - start) };
  const best = (area: Rectangle) => {
    let winner: PlaceTitlePlacement | null = null;
    let distance = Infinity;
    for (const clear of clearAreas(area, expanded)) {
      const scale = Math.min(1, clear.width / text.width, clear.height / text.height);
      if (scale <= 0) continue;
      const width = text.width * scale;
      const height = text.height * scale;
      const x = clamp(frame.width / 2 - width / 2, clear.x, right(clear) - width);
      const y = clamp(frame.height * .35 - height / 2, clear.y, bottom(clear) - height);
      const proximity = Math.hypot((x + width / 2 - frame.width / 2) / frame.width,
        (y + height / 2 - frame.height * .35) / frame.height);
      if (!winner || scale > winner.scale + .001 || (Math.abs(scale - winner.scale) <= .001 && proximity < distance)) {
        winner = { x, y, width, height, scale }; distance = proximity;
      }
    }
    return winner;
  };
  const upper = best(preferred);
  if (upper && upper.scale >= .65) return upper;
  const lower = best(belowTitle);
  if (lower && lower.scale >= .65) return lower;
  return best(safe);
}
